/**
 * Nested T-Hangar Edge Classification — Interleaving Pattern
 *
 * In the interleaving layout, the wall geometry is fundamentally different
 * from standard Z-partitions. ALL 6 partition edges are marked 'internal'
 * (suppressed) and the NestedTRowOrchestrator renders all partition walls
 * explicitly with correct interleaving geometry.
 *
 * Only front (door) and back (tail end) walls are rendered per-unit.
 */

import type { THangarUnitEdge } from '../standard-t-hangar/edges'
import type { THangarParams } from '../standard-t-hangar/params'
import { calculateStepWidth } from '../standard-t-hangar/params'
import type { DoorSpec } from '../types'

/**
 * Create T-hangar edges for a nested interleaving unit.
 *
 * Front + back are 'exterior' (rendered by TShapedUnitComposer).
 * All 6 partition edges are 'internal' (suppressed — orchestrator handles them).
 */
export function createNestedTHangarEdges(
  _unitIndex: number,
  _unitCount: number,
  params: THangarParams,
  _buildingCode: string,
  doorSpec?: DoorSpec,
  tailDepthFt?: number
): THangarUnitEdge[] {
  const stepWidth = calculateStepWidth(params)
  const tailDepth = tailDepthFt ?? params.wing_depth_ft

  const frontDoorSpec: DoorSpec = doorSpec ?? {
    offset_ft: (params.unit_width_ft - Math.min(params.default_door_width_ft, params.unit_width_ft - 4)) / 2,
    width_ft: Math.min(params.default_door_width_ft, params.unit_width_ft - 4),
    height_ft: params.default_door_height_ft,
    type: params.default_door_type,
  }

  return [
    // Front — exterior with door
    {
      id: 'front',
      type: 'exterior',
      canBeShared: false,
      openings: [frontDoorSpec],
      length_ft: params.unit_width_ft,
      height_ft: params.height_ft,
    },
    // Back — exterior, tail end
    {
      id: 'back',
      type: 'exterior',
      canBeShared: false,
      length_ft: params.tail_width_ft,
      height_ft: params.height_ft,
    },
    // All 6 partition edges → internal (orchestrator handles walls)
    { id: 'right_wing', type: 'internal', canBeShared: false, length_ft: params.wing_depth_ft, height_ft: params.height_ft },
    { id: 'right_step', type: 'internal', canBeShared: false, length_ft: stepWidth, height_ft: params.height_ft },
    { id: 'right_tail', type: 'internal', canBeShared: false, length_ft: tailDepth, height_ft: params.height_ft },
    { id: 'left_wing', type: 'internal', canBeShared: false, length_ft: params.wing_depth_ft, height_ft: params.height_ft },
    { id: 'left_step', type: 'internal', canBeShared: false, length_ft: stepWidth, height_ft: params.height_ft },
    { id: 'left_tail', type: 'internal', canBeShared: false, length_ft: tailDepth, height_ft: params.height_ft },
  ]
}
